/**
 * QueueStatsCards - Cards de KPI da Fila de Atendimento
 *
 * Design "Linear minimalist enterprise": cartão branco minimalista com
 * borda superior colorida (2px) alinhada às cores das colunas do Kanban,
 * label em caixa alta, valor numérico em destaque e ícone à direita.
 */

import {
  Users as UsersIcon,
  Monitor,
  Clock,
  ArrowLeftRight,
  Zap,
  type LucideIcon,
} from 'lucide-react';
import { Skeleton } from '@/components/ui/skeleton';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { QueueStats } from '@/hooks/useServiceQueue';
import { cn } from '@/lib/utils';

interface QueueStatsCardsProps {
  stats: QueueStats;
  loading?: boolean;
}

/** Formata tempo de espera em formato legível (s / m / h / d) */
function formatWaitingTime(seconds: number): string {
  if (seconds < 60) return `${seconds}s`;
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m`;
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  if (hours < 24) return minutes > 0 ? `${hours}h ${minutes}m` : `${hours}h`;
  const days = Math.floor(hours / 24);
  return `${days}d ${hours % 24}h`;
}

/** Formata números grandes com separador de milhar pt-BR (3.768) */
function formatNumber(value: number | string): string {
  if (typeof value === 'string') return value;
  return value.toLocaleString('pt-BR');
}

/** Paletas semânticas alinhadas às colunas do Kanban */
type AccentKey = 'neutral' | 'purple' | 'orange' | 'blue' | 'emerald';

const ACCENT_STYLES: Record<AccentKey, { topBorder: string; icon: string }> = {
  neutral: { topBorder: 'border-t-neutral-900 dark:border-t-neutral-100', icon: 'text-neutral-500' },
  purple: { topBorder: 'border-t-purple-500', icon: 'text-purple-500' },
  orange: { topBorder: 'border-t-orange-500', icon: 'text-orange-500' },
  blue: { topBorder: 'border-t-blue-500', icon: 'text-blue-500' },
  emerald: { topBorder: 'border-t-emerald-500', icon: 'text-emerald-500' },
};

interface KpiCardProps {
  label: string;
  value: string;
  icon: LucideIcon;
  accent: AccentKey;
  tooltip?: string;
}

/** Card KPI individual — borda superior colorida + label + valor + ícone */
function KpiCard({ label, value, icon: Icon, accent, tooltip }: KpiCardProps) {
  const styles = ACCENT_STYLES[accent];

  const card = (
    <div
      className={cn(
        'bg-card border border-border rounded-lg px-4 py-2 shadow-sm',
        'flex flex-col transition-all duration-200 hover:shadow-md',
        'border-t-2',
        styles.topBorder,
      )}
    >
      <div className="flex items-center justify-between">
        <span className="text-[10px] font-medium text-muted-foreground uppercase tracking-wider truncate">
          {label}
        </span>
        <Icon className={cn('w-3.5 h-3.5 shrink-0', styles.icon)} aria-hidden="true" />
      </div>
      <div className="text-lg font-bold text-foreground tracking-tight tabular-nums">
        {value}
      </div>
    </div>
  );

  if (!tooltip) return card;

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <div className="cursor-help">{card}</div>
        </TooltipTrigger>
        <TooltipContent side="bottom" className="max-w-xs">
          <p className="text-xs">{tooltip}</p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

export function QueueStatsCards({ stats, loading }: QueueStatsCardsProps) {
  // Configuração dos 5 KPIs — cores alinhadas às colunas do Kanban
  const cards: KpiCardProps[] = [
    {
      label: 'Total na Fila',
      value: formatNumber(stats.total),
      icon: UsersIcon,
      accent: 'neutral',
    },
    {
      label: 'No Bot',
      value: formatNumber(stats.waiting_bot),
      icon: Monitor,
      accent: 'purple',
    },
    {
      label: 'Aguardando Agente',
      value: formatNumber(stats.waiting_agent),
      icon: Clock,
      accent: 'orange',
    },
    {
      label: 'Transferências',
      value: formatNumber(stats.pending_transfers),
      icon: ArrowLeftRight,
      accent: 'blue',
    },
    {
      label: 'Tempo Médio',
      value: formatWaitingTime(stats.avg_waiting_time),
      icon: Zap,
      accent: 'emerald',
      tooltip: 'Tempo médio entre a chegada da conversa e o início do atendimento pelo agente',
    },
  ];

  if (loading) {
    return (
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-2 sm:gap-3">
      {Array.from({ length: 5 }).map((_, i) => (
          <Skeleton key={i} className="h-[50px] rounded-lg" />
        ))}
      </div>
    );
  }

  return (
    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-2 sm:gap-3">
      {cards.map((card) => (
        <KpiCard key={card.label} {...card} />
      ))}
    </div>
  );
}
